import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, X, MessageCircle } from "lucide-react";
import { SERVICES, whatsappLink, ANALYTICS_EVENTS, WHATSAPP_DEFAULT_MESSAGE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const track = (name, detail = {}) =>
  window.dispatchEvent(new CustomEvent("spa-analytics", { detail: { name, ...detail } }));

export default function EnquiryModal({ service, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: service || "General spa enquiry",
    preferred_date: "",
    preferred_time: "",
    guests: "1",
    consent: false,
    website: "",
  });
  const [state, setState] = useState("idle");
  const update = (key) => (event) =>
    setForm({ ...form, [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value });

  const whatsappMessage = () => {
    const parts = [
      `Hello, I'd like to enquire about ${form.service} at Zorba International Spa.`,
      form.preferred_date ? `Preferred date: ${form.preferred_date}` : null,
      form.preferred_time ? `Preferred time: ${form.preferred_time}` : null,
      form.guests ? `Guests: ${form.guests}` : null,
      form.name ? `Name: ${form.name}` : null,
    ].filter(Boolean);
    return parts.join("\n");
  };

  async function submit(event) {
    event.preventDefault();
    setState("loading");
    track(ANALYTICS_EVENTS.bookSubmit);
    try {
      await axios.post(`${API}/enquiries`, form);
      setState("success");
      track(ANALYTICS_EVENTS.bookSaved);
    } catch {
      setState("error");
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="enquiry-title"
        data-testid="enquiry-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="icon-btn modal-close" onClick={onClose} aria-label="Close enquiry form" data-testid="enquiry-close-button">
          <X />
        </button>
        {state === "success" ? (
          <div className="success-state">
            <div className="success-icon"><Check /></div>
            <h2 id="enquiry-title" data-testid="enquiry-success-message">Enquiry saved</h2>
            <p>Your request is ready to share with our featured spa partner. Continue on WhatsApp to choose a time.</p>
            <a
              className="btn btn-primary"
              href={whatsappLink(whatsappMessage() || WHATSAPP_DEFAULT_MESSAGE)}
              target="_blank"
              rel="noreferrer"
              data-testid="success-whatsapp-link"
              onClick={() => track(ANALYTICS_EVENTS.whatsappClick, { source: "post_submit" })}
            >
              <MessageCircle size={17} /> Continue in WhatsApp
            </a>
            <button className="text-link" onClick={onClose} data-testid="enquiry-done-button">Done</button>
          </div>
        ) : (
          <>
            <p className="eyebrow">Private enquiry</p>
            <h2 id="enquiry-title">Plan your wellness visit</h2>
            <p className="muted">Share the basics and we'll help you take the next step. No obligation to book.</p>
            <form onSubmit={submit} data-testid="enquiry-form">
              <label>
                Name
                <input required value={form.name} onChange={update("name")} data-testid="enquiry-name-input" placeholder="Your name" />
              </label>
              <label>
                Phone
                <input required value={form.phone} onChange={update("phone")} data-testid="enquiry-phone-input" placeholder="Your phone number" inputMode="tel" />
              </label>
              <label>
                Service
                <select value={form.service} onChange={update("service")} data-testid="enquiry-service-select">
                  <option>General spa enquiry</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug}>{s.name}</option>
                  ))}
                </select>
              </label>
              <div className="form-grid">
                <label>
                  Preferred date
                  <input type="date" value={form.preferred_date} onChange={update("preferred_date")} data-testid="enquiry-date-input" />
                </label>
                <label>
                  Preferred time
                  <input type="time" value={form.preferred_time} onChange={update("preferred_time")} data-testid="enquiry-time-input" />
                </label>
              </div>
              <label>
                Guests
                <select value={form.guests} onChange={update("guests")} data-testid="enquiry-guests-select">
                  <option>1</option>
                  <option>2</option>
                  <option>3+</option>
                </select>
              </label>
              <label className="check-label">
                <input type="checkbox" required checked={form.consent} onChange={update("consent")} data-testid="enquiry-consent-checkbox" />
                I agree to be contacted about this enquiry.
              </label>
              {/* Honeypot — hidden from real users, auto-filled by bots */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                value={form.website || ""}
                onChange={update("website")}
                style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                data-testid="enquiry-honeypot"
              />
              {state === "error" && (
                <p className="error" data-testid="enquiry-error-message">We couldn't save this yet. Please try again.</p>
              )}
              <button className="btn btn-primary full" disabled={state === "loading"} data-testid="enquiry-submit-button">
                {state === "loading" ? "Saving…" : "Save enquiry & continue"} <ArrowRight size={17} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
